import React from 'react';

/**
 * Signal types supported by the pivot point visualization
 * Note: These match the database interaction_direction enum values
 */
export type SignalType = 'BULLISH_BOUNCE' | 'BEARISH_REJECTION' | 'BULLISH_BREAK' | 'BEARISH_BREAK' | 'NEUTRAL_TOUCH';

/**
 * Metadata for rendering signal icons, colors, and labels consistently across the UI
 */
export interface SignalMeta {
  key: SignalType;
  label: string;
  color: string;
  chartColor: string; // Color for chart markers
  Icon: React.FC<{ size?: number; className?: string }>;
  chartShape: 'arrowUp' | 'arrowDown';
  chartPosition: 'aboveBar' | 'belowBar';
  description: string;
}

/**
 * Triangle Up icon component for bullish signals
 */
const TriangleUp: React.FC<{ size?: number; className?: string }> = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 4L20 20H4L12 4Z" />
  </svg>
);

/**
 * Triangle Down icon component for bearish signals
 */
const TriangleDown: React.FC<{ size?: number; className?: string }> = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 20L4 4H20L12 20Z" />
  </svg>
);

/**
 * Square icon component for legacy non-directional break signals
 */
const Square: React.FC<{ size?: number; className?: string }> = ({ size = 14, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <rect x="4" y="4" width="16" height="16" />
  </svg>
);

/**
 * Centralized signal configuration
 * Single source of truth for all signal rendering across legend, chart, and modal
 * Keys match database interaction_direction enum values exactly
 */
export const SIGNAL_CONFIG: Record<SignalType, SignalMeta> = {
  BULLISH_BOUNCE: {
    key: 'BULLISH_BOUNCE',
    label: 'Bounce (Support)',
    color: '#a6dac9', // UI display color (lighter green)
    chartColor: '#6fc597', // Chart marker color (bright green)
    Icon: TriangleUp,
    chartShape: 'arrowUp',
    chartPosition: 'belowBar',
    description: 'Price bounced off support level, indicating buying pressure',
  },
  BEARISH_REJECTION: {
    key: 'BEARISH_REJECTION',
    label: 'Rejection (Resistance)',
    color: '#da7c7c', // UI display color (lighter red)
    chartColor: '#cc4747', // Chart marker color (bright red)
    Icon: TriangleDown,
    chartShape: 'arrowDown',
    chartPosition: 'aboveBar',
    description: 'Price rejected at resistance level, indicating selling pressure',
  },
  BULLISH_BREAK: {
    key: 'BULLISH_BREAK',
    label: 'Bullish Break',
    color: '#10b981', // UI display color (emerald)
    chartColor: '#00FF00', // Chart marker color (bright green)
    Icon: TriangleUp,
    chartShape: 'arrowUp',
    chartPosition: 'aboveBar',
    description: 'Price broke above resistance level - bullish breakout signal',
  },
  BEARISH_BREAK: {
    key: 'BEARISH_BREAK',
    label: 'Bearish Break',
    color: '#dc2626', // UI display color (darker red)
    chartColor: '#FF0000', // Chart marker color (bright red)
    Icon: TriangleDown,
    chartShape: 'arrowDown',
    chartPosition: 'belowBar',
    description: 'Price broke below support level - bearish breakdown signal',
  },
  NEUTRAL_TOUCH: {
    key: 'NEUTRAL_TOUCH',
    label: 'Touch (Neutral)',
    color: '#8B5CF6', // UI display color (purple)
    chartColor: '#8B5CF6', // Chart marker color (purple)
    Icon: Square,
    chartShape: 'arrowUp',
    chartPosition: 'belowBar',
    description: 'Price touched pivot level without clear directional bias',
  },
};

/**
 * Array of all signal types in display order (excludes NEUTRAL_TOUCH as it's filtered out by API)
 */
export const SIGNAL_TYPES: SignalType[] = ['BULLISH_BOUNCE', 'BEARISH_REJECTION', 'BULLISH_BREAK', 'BEARISH_BREAK'];

/**
 * Get signal metadata by type string (case-insensitive)
 */
export function getSignalMeta(type: string): SignalMeta | null {
  const normalizedType = type.toUpperCase() as SignalType;
  return SIGNAL_CONFIG[normalizedType] || null;
}

/**
 * Get chart marker color for a signal type
 */
export function getSignalChartColor(type: string): string {
  const meta = getSignalMeta(type);
  return meta?.chartColor || '#808080';
}

/**
 * Get UI display color for a signal type
 */
export function getSignalColor(type: string): string {
  const meta = getSignalMeta(type);
  return meta?.color || '#94a3b8';
}

